import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { isNil } from "lodash";
import Skeleton from "@mui/material/Skeleton";
import { User } from "interfaces/user";
import { CaseEvent, CaseEventType } from "interfaces/case-event";
import { DiagnosticReportCard } from "components/CaseManagement/CaseStream/EventCards/DiagnosticReportCard";
import { ReportCard } from "components/CaseManagement/CaseStream/EventCards/ReportCard";
import { PostCard } from "components/CaseManagement/CaseStream/EventCards/PostCard";

interface GetEventResponse {
  loading: boolean;
  error: boolean;
  caseEvent: CaseEvent | undefined;
  refetchCaseEvent(): Promise<void>;
}

const useGetCaseEvent = (entityId: number): GetEventResponse => {
  const { loading, error, response, refetch } = useFetchGetV2(
    `events/${entityId}`,
    isNil(entityId)
  );

  return {
    loading,
    error,
    caseEvent: response as CaseEvent | undefined,
    refetchCaseEvent: refetch
  };
};

export interface EventCardProps {
  caseEventId: number;
  user: User;
  datetime: string;
  refetchStream(): Promise<void>;
}

export const EventCard = ({
  caseEventId,
  user,
  datetime,
  refetchStream
}: EventCardProps): JSX.Element => {
  const { loading, error, caseEvent, refetchCaseEvent } = useGetCaseEvent(
    caseEventId
  );

  if (loading || error || !caseEvent)
    return <Skeleton variant="rectangular" height={150} />;

  switch (caseEvent.type) {
    case CaseEventType.DIAGNOSTIC:
      return (
        <DiagnosticReportCard
          caseEvent={caseEvent}
          user={user}
          datetime={datetime}
          refetchCaseEvent={refetchCaseEvent}
          refetchStream={refetchStream}
        />
      );
    case CaseEventType.INSPECTION:
      return (
        <ReportCard
          caseEvent={caseEvent}
          user={user}
          refetchCaseEvent={refetchCaseEvent}
          refetchStream={refetchStream}
          datetime={datetime}
          reportName="Inspection Report"
          reportType="inspection"
          severity={caseEvent.case_severity_type}
        />
      );
    case CaseEventType.REPAIR:
      return (
        <ReportCard
          caseEvent={caseEvent}
          user={user}
          refetchCaseEvent={refetchCaseEvent}
          refetchStream={refetchStream}
          datetime={datetime}
          reportName="Repair Report"
          reportType="repair"
          severity={caseEvent.case_severity_type}
        />
      );
    case CaseEventType.POST:
      return (
        <PostCard
          caseEvent={caseEvent}
          user={user}
          datetime={datetime}
          refetchStream={refetchStream}
        />
      );
    default:
      console.error(`Unhandled event type ${caseEvent.type}`);
      return <></>;
  }
};
